import GameServerPublicInfo from "./models/GameServerPublicInfo";
import CommonResponseWithPlayerAuthInfo from "./models/CommonResponseWithPlayerAuthInfo";
import PlayerAuthInfo from "./models/PlayerAuthInfo";
import Cookies from 'js-cookie';
import CommonResponseWithSteamPlayerPublicInfo from "./models/CommonResponseWithSteamPlayerPublicInfo";
import CommonResponse from "./models/CommonResponse";
import PlayingPlayersResponse from "./models/player/PlayingPlayersResponse";
import PlayerAvailablePOIsDTO from "./models/teleport/PlayerAvailablePOIsDTO";
import OptionalGenericPOIDTO from "./models/teleport/OptionalGenericPOIDTO";
import DoTeleportRequestDTO from "./models/teleport/DoTeleportRequestDTO";
import PublicTerritoryDTO from "./models/territory/PublicTerritoryDTO";
import ExpansionQuestDTO from "./models/quest/ExpansionQuestDTO";
import ExpansionQuestNPCDTO from "./models/quest/ExpansionQuestNPCDTO";
import {getServerPublicInfo} from "./calls/server_public/getServerPublicInfo";
import {tryToDoLoginWithSteamReturnInfo} from "./calls/user_public/tryToDoLoginWithSteamReturnInfo";
import {setServerRunningValues} from "./calls/user_private_admin/setServerRunningValues";
import {triggerRebootServer} from "./calls/user_private_admin/triggerRebootServer";
import {getQuests} from "./calls/user_private_quester/getQuests";
import {getQuestForEdit} from "./calls/user_private_quester/getQuestForEdit";
import {getNPCs} from "./calls/user_private_quester/getNPCs";
import {setTerritorySpawnLocation} from "./calls/user_private/setTerritorySpawnLocation";
import {doTeleport} from "./calls/user_private/doTeleport";
import {amIAtATeleportingPOI} from "./calls/user_private/amIAtATeleportingPOI";
import {getMyTeleportingPOIs} from "./calls/user_private/getMyTeleportingPOIs";
import {repairMyBag} from "./calls/user_private/repairMyBag";
import {retrieveMySteamInfo} from "./calls/user_private/retrieveMySteamInfo";
import {listTerritories} from "./calls/user_public/listTerritories";
import {getPlayingPlayers} from "./calls/user_private_admin/getPlayingPlayers";
import PlayersResponse from "./models/player/PlayersResponse";
import {getPlayers} from "./calls/user_private_admin/getPlayers";
import {getExpansionColors} from "./calls/server_public/getExpansionColors";
import {getExpansionIcons} from "./calls/server_public/getExpansionIcons";
import {sendNotificationToPlayer} from "./calls/user_private_admin/sendNotificationToPlayer";

class BackendClient {
  public static readonly GetServerPublicInfoPath = "/api/server/getServerPublicInfo"
  public static readonly GetPublicExpansionIcons = "/api/server/getPublicExpansionIcons"
  public static readonly GetPublicExpansionColors = "/api/server/getPublicExpansionColors"
  public static readonly TriggerRebootPath = "/api/player/triggerServerUpdateForcedManually"
  public static readonly SetServerRunningValues = "/api/player/setServerRunningValues"
  public static readonly SendNotificationToPlayer = "/api/player/sendNotificationToPlayer"
  public static readonly ListPlayers = "/api/player/listPlayers"
  public static readonly ListPlayingPlayers = "/api/player/listPlayingPlayers"
  public static readonly LoginWithSteamReturnInfoPath = "/api/player/loginWithSteamReturnInfo"
  public static readonly RetrieveMySteamInfo = "/api/player/retrieveMySteamInfo"
  public static readonly RepairMyVipBag = "/api/player/repairMyVipBag"
  public static readonly GetMyTeleportingPOIs = "/api/player/getMyTeleportingPOIs"
  public static readonly AmIAtATeleportingPOI = "/api/player/amIAtATeleportingPOI"
  public static readonly DoTeleport = "/api/player/doTeleport"
  public static readonly SetTerritorySpawnLocation = "/api/player/setTerritorySpawnLocation"
  public static readonly ListTerritories = "/api/player/listTerritories"
  public static readonly GetQuests = "/api/quest/getQuests"
  public static readonly GetQuestForEdit = "/api/quest/getQuestForEdit"
  public static readonly GetNPCs = "/api/quest/getNPCs"

  private readonly backendUrl;
  private readonly CK_authToken = 'auth-token';
  private readonly CK_authEntity = 'auth-entity';
  private readonly CK_loggedUserRoles = 'logged-user-roles';

  private readonly ROLE_ADMIN = 'ADMIN';
  private readonly ROLE_QUESTER = 'QUESTER';

  private authListeners: Map<string, (playerAuth: PlayerAuthInfo | null) => void> = new Map();

  protected authToken: string | null = null;
  private authEntity: string | null = null;
  private loggedUserRoles: Set<string> | null = null;

  constructor() {
    this.backendUrl = process.env.REACT_APP_BACKEND_URL
    const authToken = Cookies.get(this.CK_authToken)
    if (authToken !== undefined) {
      this.authToken = authToken;
    }
    const authEntity = Cookies.get(this.CK_authEntity);
    if (authEntity !== undefined) {
      this.authEntity = authEntity;
    }

    const loggedUserRoles = Cookies.get(this.CK_loggedUserRoles);
    if (loggedUserRoles !== undefined) {
      this.loggedUserRoles = new Set(JSON.parse(loggedUserRoles));
    }
  }

  wireNewPlayerAuthListener(listenerName: string, callbackFunction: (playerAuth: PlayerAuthInfo | null) => void) {
    this.authListeners.set(listenerName, callbackFunction);
  }

  private notifyAuthListeners(playerAuth: PlayerAuthInfo | null) {
    this.authListeners.forEach(callbackFunction => {
      callbackFunction(playerAuth);
    });
  }

  getBackendUrl(): string | undefined {
    return this.backendUrl;
  }

  hasPlayerAuth(): boolean {
    return this.authToken !== null && this.authEntity !== null;
  }

  isAdmin(): boolean {
    return this.loggedUserRoles !== null && this.loggedUserRoles.has(this.ROLE_ADMIN);
  }

  isQuester(): boolean {
    return this.loggedUserRoles !== null && (this.loggedUserRoles.has(this.ROLE_QUESTER) || this.loggedUserRoles.has(this.ROLE_ADMIN));
  }

  getAuthEntity(): string | null {
    return this.authEntity;
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  wirePlayerAuth(auth: PlayerAuthInfo) {
    Cookies.set(this.CK_authToken, auth.token, { expires: new Date(auth.validity) });
    Cookies.set(this.CK_authEntity, auth.entity, { expires: new Date(auth.validity) });
    Cookies.set(this.CK_loggedUserRoles, JSON.stringify(auth.roles), { expires: new Date(auth.validity) });

    this.authToken = auth.token;
    this.authEntity = auth.entity;
    this.loggedUserRoles = new Set(auth.roles);

    this.notifyAuthListeners(auth);
  }

  clearPlayerAuth() {
    Cookies.remove(this.CK_authToken)
    this.authToken = null;
    Cookies.remove(this.CK_authEntity)
    this.authEntity = null;
    Cookies.remove(this.CK_loggedUserRoles)
    this.loggedUserRoles = null;

    this.notifyAuthListeners(null);
  }

  // Below are the API calls

  // Server Public
  getServerPublicInfo(): Promise<GameServerPublicInfo> {
    return getServerPublicInfo(this);
  }
  getExpansionColors(): Promise<Array<string>> {
    return getExpansionColors(this);
  }
  getExpansionIcons(): Promise<Array<string>> {
    return getExpansionIcons(this);
  }

  // User Public
  tryToDoLoginWithSteamReturnInfo(steamReturnInfo: string): Promise<CommonResponseWithPlayerAuthInfo> {
    return tryToDoLoginWithSteamReturnInfo(this, steamReturnInfo);
  }
  listTerritories(includeDeleted: boolean): Promise<Array<PublicTerritoryDTO>> {
    return listTerritories(this, includeDeleted);
  }

  // User Private
  retrieveMySteamInfo(): Promise<CommonResponseWithSteamPlayerPublicInfo> {
    return retrieveMySteamInfo(this);
  }
  repairMyBag(): Promise<CommonResponse> {
    return repairMyBag(this);
  }
  getMyTeleportingPOIs(): Promise<PlayerAvailablePOIsDTO> {
    return getMyTeleportingPOIs(this);
  }
  amIAtATeleportingPOI(): Promise<OptionalGenericPOIDTO> {
    return amIAtATeleportingPOI(this);
  }
  doTeleport(request: DoTeleportRequestDTO): Promise<CommonResponse> {
    return doTeleport(this, request);
  }
  setTerritorySpawnLocation(): Promise<CommonResponse> {
    return setTerritorySpawnLocation(this);
  }

  // User Private (Quester)
  getQuests(): Promise<Array<ExpansionQuestDTO>> {
    return getQuests(this)
  }
  getQuestForEdit(questId: number): Promise<ExpansionQuestDTO> {
    return getQuestForEdit(this, questId);
  }
  getNPCs(): Promise<Array<ExpansionQuestNPCDTO>> {
    return getNPCs(this);
  }

  // User Private (Admin)
  setServerRunningValues(debugFlag: boolean): Promise<CommonResponse> {
    return setServerRunningValues(this, debugFlag);
  }
  triggerRebootServer(reason: string): Promise<CommonResponse> {
    return triggerRebootServer(this, reason)
  }
  getPlayingPlayers(): Promise<PlayingPlayersResponse> {
    return getPlayingPlayers(this);
  }
  sendNotificationToPlayer(bohId: string, title: string, bodyText: string, icon: string, color: string, duration: number): Promise<CommonResponse> {
    return sendNotificationToPlayer(this, bohId, title, bodyText, icon, color, duration);
  }
  getPlayers(steamIdFilter: string, bohIdFilter: string, beGuidFilter: string, isPremiumFilter: boolean | null, profileFilter: string, pageSize: number, pageNumber: number): Promise<PlayersResponse> {
    return getPlayers(this, steamIdFilter, bohIdFilter, beGuidFilter, isPremiumFilter, profileFilter, pageSize, pageNumber);
  }

}

export default BackendClient;